<template>
  <div :element-loading-text="loadingText" class="KBO_Table">
    <div v-if="hasBefore">
      <slot name="before" />
    </div>
    <div v-if="hasTitle" :class="{ tall: tallHeader }" class="KBO_Table__title">
      <div class="KBO_Table__title__text">
        <slot name="title">{{ title }}</slot>
      </div>
      <div v-if="hasHeaderActions" class="KBO_Table__title__actions">
        <slot name="header-actions"></slot>

        <div v-if="selectable && selectedRows.length > 0" class="mr-2">
          <el-dropdown trigger="click" @command="handleSelectedCommand">
            <span class="el-dropdown-link">
              <span>
                {{ selectedRows.length }}
                {{ elementsLabel }}
                seleccionados
              </span>
              <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="action in selectedActions"
                :key="action.key"
                :command="action.key"
                :icon="action.icon"
                :style="action.style"
                >{{ action.label }}</el-dropdown-item
              >
            </el-dropdown-menu>
          </el-dropdown>
        </div>

        <div v-if="count && isTableMode" class="ml-2">
          {{ items.length }}
          {{ elementsLabel }}
        </div>

        <div
          v-if="(exportable || pdfExportable || excelExportable) && isTableMode"
          :class="{ 'mr-2': hasStats || hasMap }"
          class="ml-3 export"
        >
          <el-tooltip v-if="exportable || excelExportable" content="Exportar a Excel">
            <el-button
              :disabled="items.length < 1"
              class="py-0 my-0"
              size="mini"
              type="text"
              @click="handleExportXLSX"
            >
              <i class="fas fa-file-excel"></i>
            </el-button>
          </el-tooltip>

          <el-tooltip v-if="exportable || pdfExportable" content="Exportar a PDF">
            <el-button
              :disabled="items.length < 1"
              class="py-0 my-0"
              size="mini"
              type="text"
              @click="handleExportPDF"
            >
              <i class="fas fa-file-pdf"></i>
            </el-button>
          </el-tooltip>
        </div>

        <div v-if="hasStats || hasMap" class="ml-2">
          <el-button
            :class="{ active: isTableMode }"
            class="py-0 my-0 table-button"
            size="mini"
            type="text"
            @click="setTableMode"
          >
            Tabla <span class="material-icons m-icon-s">table_rows</span>
          </el-button>
        </div>
        <div v-if="hasStats" class="ml-2">
          <el-button
            :class="{ active: isStatsMode }"
            class="py-0 my-0"
            size="mini"
            type="text"
            @click="setStatsMode"
          >
            Gráficas <span class="material-icons m-icon-s">insights</span>
          </el-button>
        </div>
        <div v-if="hasMap" class="ml-2">
          <el-button
            :class="{ active: isMapMode }"
            class="py-0 my-0 table-button"
            size="mini"
            type="text"
            @click="setMapMode"
          >
            Mapa <span class="material-icons m-icon-s">map</span>
          </el-button>
        </div>
      </div>
    </div>

    <template v-if="isTableMode">
      <div v-if="hasBeforeTable" class="mb-1">
        <slot name="beforeTable" />
      </div>
      <template>
        <div :style="gridStyle" class="KBO_Table__header">
          <div
            v-for="column in visibleColumns"
            :key="`header_${column.key}`"
            :style="column.style || {}"
            class="KBO_Table__header__col"
          >
            <div v-if="column.key === '_selector'">
              <el-checkbox :value="allSelected" @change="toggleAllSelected" />
            </div>
            <slot v-else :name="`header_${column.key}`">
              <span v-html="column.label" />
            </slot>
          </div>
        </div>

        <RecycleScroller
          v-if="items && items.length > 0"
          ref="recyclerScroller"
          v-loading="loading"
          :item-size="rowHeight"
          :items="items"
          :key-field="rowKeyField"
          class="KBO_Table__scroller"
        >
          <template v-slot="{ item }">
            <div
              :class="{
                clickable: $listeners.rowClicked,
                ...calculateRowClass(item),
              }"
              :style="{ ...calculateRowStyle(item), ...gridStyle }"
              class="KBO_Table__row"
              @click="handleRowClick(item)"
            >
              <div
                v-for="column in visibleColumns"
                :key="`row_${item[rowKeyField]}_col_${column.key}`"
                :class="{ clickable: column.onClick }"
                :style="column.style || {}"
                class="KBO_Table__row__col"
                @click="handleColumnClick(column, item, $event)"
              >
                <div v-if="column.key === '_selector'">
                  <el-checkbox
                    :value="isSelected(item[rowKeyField])"
                    @change="toggleSelected(item[rowKeyField])"
                  />
                </div>
                <slot v-else :item="item" :name="`column_${column.key}`">
                  <el-tooltip v-if="showTooltips" :open-delay="500" placement="top-start">
                    <div slot="content" v-html="getColumnValue(item, column)" />
                    <span class="text" v-html="getColumnValue(item, column)" />
                  </el-tooltip>
                  <span v-else class="text" v-html="getColumnValue(item, column)" />
                </slot>
              </div>
            </div>
          </template>
        </RecycleScroller>
        <div v-else v-loading="loading" class="KBO_Table__empty">
          <slot name="empty">No hay elementos que mostrar</slot>
        </div>
      </template>
    </template>
    <div v-if="isStatsMode" class="stats">
      <slot name="stats">No se ha configurado el dashboard</slot>
    </div>
    <div v-if="isMapMode" class="map">
      <slot name="map">No se ha configurado el mapa</slot>
    </div>
  </div>
</template>

<script>
import { get } from "lodash/object";
import { generateExcel, generatePDF } from "@/utils/export";
import moment from "@/utils/moment";
import { mapGetters } from "vuex";
import cache from "@/mixins/cache";
import flavours from "@/mixins/flavours";

const TABLE_MODE = "TABLE";
const STATS_MODE = "STATS";
const MAP_MODE = "MAP";

export default {
  name: "KboTable",

  props: {
    items: Array,
    tallHeader: {
      type: Boolean,
      required: false,
    },
    showTooltips: {
      type: Boolean,
      required: false,
    },
    highlightActive: {
      type: Boolean,
      required: false,
    },
    count: {
      type: Boolean,
      required: false,
    },
    exportable: {
      type: Boolean,
      required: false,
    },
    pdfExportable: {
      type: Boolean,
      required: false,
    },
    excelExportable: {
      type: Boolean,
      required: false,
    },
    pdfOrientation: {
      type: String,
      default: "landscape",
    },
    rowKeyField: {
      type: String,
      default: "id",
    },
    rowHeight: {
      type: Number,
      default: 24,
    },
    scroll: {
      type: Number,
      required: false,
    },
    rowStyle: {
      type: [Object, Function],
      required: false,
    },
    rowClass: {
      type: [Object, Function],
      required: false,
    },
    elementsLabel: {
      type: String,
      default: "elementos",
      required: false,
    },
    tableStyle: {
      type: Object,
      required: false,
    },
    tableName: {
      type: String,
      default: "Table",
    },
    statsDashboard: {
      type: Object,
      required: false,
    },
    tableDescription: {
      type: String,
      required: false,
    },
    centralId: {
      type: Number,
      required: false,
    },
    workgroupId: {
      type: Number,
      required: false,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    loadingText: {
      type: String,
      required: false,
    },
    columns: {
      type: Array,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    selectable: {
      type: Boolean,
    },
    selectedActions: {
      type: Array,
      default: () => [],
    },
    activeRow: {
      type: [String, Number],
      required: false,
    },
  },

  mixins: [!window.localStorage.getItem("NO_CACHE") ? cache : undefined, flavours].filter(Boolean),

  data() {
    return {
      selected: null,
      mode: TABLE_MODE,
      scrollPosition: null,
      selectedRows: [],
    };
  },

  computed: {
    ...mapGetters("operators", ["operator"]),
    ...mapGetters("app", ["workgroups", "centralsByWorkgroupId"]),
    centralOrWorkgroupName() {
      if (!this.workgroupId) return "";
      if (this.centralId) {
        return this.centralsByWorkgroupId(this.workgroupId).find((c) => c.id === this.centralId)
          .name;
      }
      return this.workgroups.find((wg) => wg.id === this.workgroupId).name;
    },
    tableColumns() {
      return this.selectable
        ? [
            {
              key: "_selector",
              label: "",
              width: "26px",
              onClick: (_, event) => {
                event.stopPropagation();
              },
            },
            ...this.columns,
          ]
        : this.columns;
    },
    hasHeaderActions() {
      return (
        !!this.$slots["header-actions"] ||
        this.exportable ||
        this.excelExportable ||
        this.pdfExportable ||
        this.count
      );
    },
    hasTitle() {
      return (
        !!this.$slots.title ||
        !!this.$slots["header-actions"] ||
        this.exportable ||
        this.excelExportable ||
        this.pdfExportable ||
        this.count ||
        this.title
      );
    },
    hasBefore() {
      return !!this.$slots.before;
    },
    hasBeforeTable() {
      return !!this.$slots.beforeTable;
    },
    hasStats() {
      return !!this.$slots.stats;
    },
    hasMap() {
      return !!this.$slots.map;
    },
    isMapMode() {
      return this.mode === MAP_MODE;
    },
    isTableMode() {
      return this.mode === TABLE_MODE;
    },
    isStatsMode() {
      return this.mode === STATS_MODE;
    },
    gridStyle() {
      return {
        display: "grid",
        gridTemplateColumns: this.visibleColumns.map((c) => c.width).join(" "),
      };
    },
    visibleColumns() {
      return this.tableColumns.filter((v) => {
        if (v.visible && typeof v.visible === "function") {
          return v.visible();
        } else if (typeof v.visible === "boolean") {
          return v.visible;
        }
        return true;
      });
    },
    allSelected() {
      return this.selectedRows.length === this.items.length;
    },
  },

  mounted() {
    if (this.scrollPosition) {
      setTimeout(() => {
        this.restoreScroll();
      }, 1);
    }
  },

  methods: {
    isSelected(key) {
      return this.selectedRows.includes(key);
    },
    toggleSelected(key) {
      if (this.isSelected(key)) {
        this.selectedRows = this.selectedRows.filter((k) => k !== key);
      } else {
        this.selectedRows.push(key);
      }
    },
    toggleAllSelected() {
      if (this.allSelected) {
        this.selectedRows = [];
      } else {
        this.selectedRows = this.items.map((item) => item[this.rowKeyField]);
      }
    },
    getColumnValue(row, column) {
      if (column.renderer) {
        return column.renderer(get(row, column.key), row);
      }
      return get(row, column.key);
    },
    handleRowClick(row) {
      if (this.$refs.recyclerScroller) {
        this.scrollPosition = this.$refs.recyclerScroller.$_lastUpdateScrollPosition;
      }
      if (this.$listeners.rowClicked) {
        this.selected = row[this.rowKeyField];
        this.$emit("rowClicked", row, this.items.indexOf(row));
      }
    },
    handleColumnClick(column, row, $event) {
      if (column === "_selector") return;
      if (column.setSelectedOnClick) {
        this.selected = row[this.rowKeyField];
      }
      if (column.onClick) column.onClick(row, $event);
    },
    setSelected(item) {
      this.selected = item;
    },
    cleanSelected() {
      this.selected = null;
    },
    handleSelectedCommand(command) {
      this.selectedActions.find((a) => a.key === command).handler(this.selectedRows);
      this.selectedRows = [];
    },
    calculateRowClass(row) {
      let rowClass = {};

      if (this.rowClass && typeof this.rowClass === "function") {
        rowClass = this.rowClass(row);
      } else if (this.rowClass && typeof this.rowClass === "object") {
        rowClass = this.rowClass;
      }

      if (
        (this.highlightActive && this.selected === row[this.rowKeyField]) ||
        this.activeRow === row[this.rowKeyField]
      ) {
        rowClass = { ...rowClass, active: true };
      }

      return rowClass;
    },
    calculateRowStyle(row) {
      let rowStyle = {};

      if (this.rowStyle && typeof this.rowStyle === "function") {
        rowStyle = this.rowStyle(row);
      } else if (this.rowStyle && typeof this.rowStyle === "object") {
        rowStyle = this.rowStyle;
      }

      return {
        height: `${this.rowHeight}px`,
        ...rowStyle,
      };
    },
    getExportData() {
      const columns = this.columns.filter((c) => !c.skipExport);

      const data = [];
      this.items.forEach(async (item) => {
        const rowData = [];
        columns.forEach((c) => {
          if (c.export) {
            c.export.forEach((ce) => rowData.push(ce.renderer(item)));
          } else {
            rowData.push(this.getColumnValue(item, c));
          }
        });
        data.push(rowData);
      });

      return data;
    },
    getExportHeaders() {
      const columns = this.columns.filter((c) => !c.skipExport);

      const headers = [];
      columns.forEach((c) => {
        if (c.export) {
          c.export.forEach((ce) => headers.push(ce.label));
        } else {
          headers.push(c.label);
        }
      });

      return headers;
    },
    handleExportXLSX() {
      const headers = this.getExportHeaders();
      const data = this.getExportData();
      const time = moment()().format("DD-MM-YYYY_HH-mm");
      generateExcel(`${this.tableName}-export-${time}`, "Datos", headers, data);
    },
    handleExportPDF() {
      const headers = this.getExportHeaders();
      const data = this.getExportData();
      const time = moment()().format("DD-MM-YYYY_HH-mm");
      generatePDF(
        `${this.tableName}-export-${time}`,
        {
          title: this.tableName,
          logo: this.darkLogo,
          description: this.tableDescription,
          centralOrWorkgroup: this.centralOrWorkgroupName,
          operator: this.operator.name,
        },
        headers,
        data,
        this.pdfOrientation
      );
    },
    restoreScroll(position) {
      if (this.$refs.recyclerScroller) {
        if (position) this.scrollPosition = position;
        this.$refs.recyclerScroller.scrollToPosition(this.scrollPosition);
      }
    },
    setMapMode() {
      this.$emit("changeMode", MAP_MODE);
      this.mode = MAP_MODE;
    },
    setStatsMode() {
      this.$emit("changeMode", STATS_MODE);
      this.mode = STATS_MODE;
    },
    setTableMode() {
      this.$emit("changeMode", TABLE_MODE);
      this.mode = TABLE_MODE;
    },
  },
};
</script>

<style scoped>
.export {
  display: flex;
  justify-content: center;
  align-items: center;
}

.table-button {
  position: relative;
  z-index: 100;
}

.table {
  overflow-y: auto;
}

.stats,
.map {
  height: 100%;
  overflow-y: auto;
}

.active {
  font-weight: bold;
  color: #333;
}

::v-deep .stats .el-card__body {
  height: auto !important;
}

.el-dropdown-link {
  color: #409eff;
  font-size: 12px;
  margin: 0 8px;
  cursor: pointer;
}
</style>
